import { calculateSplitMarkupSummary } from './markup';
import type {
  PackagePricingInput,
  PackagePricingResult,
  PricingCategoryAmounts,
  PricingComponent,
  VpeLine,
} from './types';

function assertMargin(marginPct: number): void {
  if (!Number.isFinite(marginPct) || marginPct < 0 || marginPct >= 100) {
    throw new Error('marginPct must be in [0, 100)');
  }
}

function assertNonNegativeFinite(value: number, name: string): void {
  if (!Number.isFinite(value) || value < 0) {
    throw new Error(`${name} must be finite and non-negative`);
  }
}

function component(cost: number, marginPct: number, btwRate: 9 | 21): PricingComponent {
  assertMargin(marginPct);
  const sellingExclBtw = cost / (1 - marginPct / 100);
  const btwEuros = sellingExclBtw * (btwRate / 100);
  return {
    cost,
    margin_pct: marginPct,
    margin_euros: sellingExclBtw - cost,
    selling_excl_btw: sellingExclBtw,
    btw_rate: btwRate,
    btw_euros: btwEuros,
    selling_incl_btw: sellingExclBtw + btwEuros,
  };
}

function mappedComponent(
  cost: number,
  marginPct: number,
  sellingExclBtw: number,
  sellingInclBtw: number,
  btwRate: 9 | 21
): PricingComponent {
  return {
    cost,
    margin_pct: marginPct,
    margin_euros: sellingExclBtw - cost,
    selling_excl_btw: sellingExclBtw,
    btw_rate: btwRate,
    btw_euros: sellingInclBtw - sellingExclBtw,
    selling_incl_btw: sellingInclBtw,
  };
}

function categoryAmounts(
  input: PackagePricingInput,
  vpeLines: VpeLine[]
): { recipe: PricingCategoryAmounts; overhead: PricingCategoryAmounts } {
  let recipeLow = 0;
  let recipeHigh = 0;
  let overheadLow = 0;
  let overheadHigh = 0;

  for (const line of vpeLines) {
    const recipe = line.quantity_per_package * input.quantity * line.unit_purchase_price;
    const overhead = line.total_purchase_cost - recipe;
    assertNonNegativeFinite(recipe, 'recipe cost');
    assertNonNegativeFinite(overhead, 'colli overhead');
    if (line.product.btw_category === 'low') {
      recipeLow += recipe;
      overheadLow += overhead;
    } else {
      recipeHigh += recipe;
      overheadHigh += overhead;
    }
  }

  return {
    recipe: { low: recipeLow, high: recipeHigh, total: recipeLow + recipeHigh },
    overhead: {
      low: overheadLow,
      high: overheadHigh,
      total: overheadLow + overheadHigh,
    },
  };
}

function total(
  components: PackagePricingResult['components']
): PackagePricingResult['total'] {
  const values = Object.values(components);
  const cost = values.reduce((sum, value) => sum + value.cost, 0);
  const marginEuros = values.reduce((sum, value) => sum + value.margin_euros, 0);
  const sellingExclBtw = values.reduce((sum, value) => sum + value.selling_excl_btw, 0);
  const btwEuros = values.reduce((sum, value) => sum + value.btw_euros, 0);
  const sellingInclBtw = values.reduce((sum, value) => sum + value.selling_incl_btw, 0);
  return {
    cost,
    margin_euros: marginEuros,
    selling_excl_btw: sellingExclBtw,
    btw_euros: btwEuros,
    selling_incl_btw: sellingInclBtw,
    weighted_margin_pct: sellingExclBtw > 0 ? (marginEuros / sellingExclBtw) * 100 : 0,
  };
}

function calculateLegacyPricing(
  input: PackagePricingInput,
  vpeLines: VpeLine[],
  amounts: ReturnType<typeof categoryAmounts>
): PackagePricingResult {
  const legacy = calculateSplitMarkupSummary(
    input,
    vpeLines,
    input.margin_low_pct,
    input.margin_high_pct
  );
  const components: PackagePricingResult['components'] = {
    low: mappedComponent(
      legacy.low.total_cost,
      legacy.low.margin_pct,
      legacy.low.selling_excl_btw,
      legacy.low.selling_incl_btw,
      9
    ),
    high: mappedComponent(
      legacy.high.total_cost,
      legacy.high.margin_pct,
      legacy.high.selling_excl_btw,
      legacy.high.selling_incl_btw,
      21
    ),
    handling: component(0, input.handling_margin_pct ?? input.margin_high_pct, 21),
    transport: component(0, input.transport_margin_pct ?? input.margin_high_pct, 21),
  };
  return {
    pricing_model_version: 'legacy_v1',
    components,
    ...amounts,
    legacy_breakdown: {
      low: {
        purchase_cost: legacy.low.purchase_cost,
        handling_cost: legacy.low.handling_cost,
        transport_cost: legacy.low.transport_cost,
      },
      high: {
        purchase_cost: legacy.high.purchase_cost,
        handling_cost: legacy.high.handling_cost,
        transport_cost: legacy.high.transport_cost,
      },
      purchase_cost: legacy.total.purchase_cost,
    },
    total: total(components),
  };
}

export function calculatePackagePricing(
  input: PackagePricingInput,
  vpeLines: VpeLine[]
): PackagePricingResult {
  assertNonNegativeFinite(input.quantity, 'quantity');
  assertNonNegativeFinite(input.handling_cost_per_package, 'handling cost');
  assertNonNegativeFinite(input.transport_cost_per_package, 'transport cost');
  const amounts = categoryAmounts(input, vpeLines);
  if (input.pricing_model_version === 'legacy_v1') {
    return calculateLegacyPricing(input, vpeLines, amounts);
  }

  const handlingMargin = input.handling_margin_pct ?? input.margin_high_pct;
  const transportMargin = input.transport_margin_pct ?? input.margin_high_pct;
  const components: PackagePricingResult['components'] = {
    low: component(amounts.recipe.low + amounts.overhead.low, input.margin_low_pct, 9),
    high: component(amounts.recipe.high + amounts.overhead.high, input.margin_high_pct, 21),
    handling: component(
      input.handling_cost_per_package * input.quantity,
      handlingMargin,
      21
    ),
    transport: component(
      input.transport_cost_per_package * input.quantity,
      transportMargin,
      21
    ),
  };

  return {
    pricing_model_version: 'component_margin_v2',
    components,
    ...amounts,
    total: total(components),
  };
}
